import {intersection} from 'lodash-es';
import {useMemo, useState} from 'react';
import styled, {css} from 'styled-components';

import {scToTwColour} from '../../theme';

import {TableHeading, StyledHeading} from './TableHeading';

import type {Normalized} from '../../types';

export interface TableProps<T> {
  headings: HeadingParams<T>[];
  collection: Normalized<T>;
  defaultSort?: TableSortBy;
  onRowClick?: (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    rowItem: T,
    idx: number
  ) => void;
  before?: React.ReactNode;
  after?: React.ReactNode;
}
export function Table<T>({
  headings,
  collection,
  ...props
}: TableProps<T>): React.JSX.Element {
  const [sortBy, setSortBy] = useState<TableSortBy | undefined>(
    props.defaultSort
  );

  const sortedRowIds: string[] = useMemo(() => {
    if (!sortBy) return collection.allIds;

    const sorted = getSortedIds(sortBy, headings, collection);
    const intersected = intersection(sorted, collection.allIds);
    return intersected;
  }, [collection, headings, sortBy]);

  const sortedRows = sortedRowIds.map((id, i) => (
    <tr
      key={id}
      tabIndex={props.onRowClick ? 0 : -1}
      role={props.onRowClick ? 'button' : ''}
      onMouseDown={(e) => e.shiftKey && e.preventDefault()}
      onClick={(e) => {
        props.onRowClick && props.onRowClick(e, collection.byId[id], i);
      }}
    >
      {headings.map(({prop, renderStrategy}) => (
        <td key={prop}>
          {renderStrategy
            ? renderStrategy(collection.byId[id], i)
            : // @ts-expect-error replacing soon
              collection.byId[id][prop]}
        </td>
      ))}
    </tr>
  ));

  const hasSetWidthColumns = headings.some((h) => !!h.width);

  return (
    <Container>
      <StyledTable $hasSetWidthColumns={hasSetWidthColumns}>
        <thead>
          <tr>
            {headings.map((heading) => (
              <TableHeading
                key={heading.prop}
                heading={heading}
                // @ts-expect-error replacing soon
                sortBy={sortBy}
                // @ts-expect-error replacing soon
                setSortBy={setSortBy}
              >
                {heading.label}
              </TableHeading>
            ))}
          </tr>
        </thead>

        <StyledTableBody
          $isClickable={Boolean(props.onRowClick)}
          $hasSetWidthColumns={hasSetWidthColumns}
        >
          {props.before || props.after || sortedRows.length ? (
            <>
              {props.before}
              {sortedRows}
              {props.after}
            </>
          ) : (
            <EmptyRow>
              <td colSpan={headings.length}>There&apos;s nothing here yet.</td>
            </EmptyRow>
          )}
        </StyledTableBody>
      </StyledTable>
    </Container>
  );
}

function getSortedIds<T>(
  sortBy: TableSortBy,
  headings: HeadingParams<T>[],
  collection: Normalized<T>
): string[] {
  const toBeSorted = headings.find((heading) => heading.prop === sortBy.prop);

  if (!toBeSorted?.sortFn) return collection.allIds;

  return toBeSorted.sortFn(collection, sortBy);
}

const Container = styled.div`
  overflow-x: scroll;

  ${({theme}) => theme.media.lg`
    overflow-x: unset;
  `}
`;
const StyledTable = styled.table<{$hasSetWidthColumns: boolean}>`
  table-layout: ${({$hasSetWidthColumns}) =>
    $hasSetWidthColumns ? 'fixed' : 'auto'};
  width: 100%;
  min-width: 1500px;
  border: none;
  text-align: left;
  border-collapse: collapse;
  white-space: nowrap;
  text-overflow: ellipsis;

  td,
  ${StyledHeading} {
    padding: 0.6rem 0.4rem;
    font-size: 0.9rem;
  }
  ${StyledHeading} button {
    margin: -0.6rem -0.4rem;
    padding: 0.6rem 0.4rem;
  }

  ${({theme}) => theme.media.lg`
    min-width: unset;
  `}
`;
const StyledTableBody = styled.tbody<{
  $isClickable: boolean;
  $hasSetWidthColumns: boolean;
}>`
  tr {
    border-top: 1px solid ${scToTwColour('theme.colours.slate6')};

    &:hover {
      cursor: ${({$isClickable}) => ($isClickable ? 'pointer' : 'unset')};
      background: ${scToTwColour('theme.colours.slate4')};
    }

    td {
      line-height: 1.4em;

      ${({$hasSetWidthColumns}) =>
        $hasSetWidthColumns &&
        css`
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        `}
    }
  }
`;
const EmptyRow = styled.tr`
  td {
    text-align: center;
  }
`;

export type HeadingParams<T> = {
  label: React.ReactNode;
  prop: string;
  width?: string;
  sortFn?: (collection: Normalized<T>, sortBy: TableSortBy) => string[];
  renderStrategy?: (value: T, idx: number) => React.ReactNode;
};
export type TableSortBy = {
  prop: string;
  isAsc: boolean;
};
